import type { GeoJSONFeature } from './ServiceAreaMapDrawer';

export interface TerritoryPaletteEntry {
  fill: string;
  stroke: string;
  badgeBg: string;
  badgeText: string;
}

export interface Territory {
  id: string;
  color: TerritoryPaletteEntry;
  /**
   * Map shape this territory was drawn from. Optional because zip-only
   * territories — created automatically when a user enters zip codes
   * before drawing anything — have no polygon yet. Render code must
   * skip features-less territories (e.g. when feeding `initialFeatures`
   * into the map drawer).
   */
  feature?: GeoJSONFeature;
  /**
   * Optional user-defined name. When unset (or blank), callers should fall
   * back to a positional label like "Territory N" via `getTerritoryDisplayName`.
   */
  name?: string;
}

/**
 * Resolve the visible label for a territory: a custom name if the user set
 * one, otherwise a positional fallback "Territory N" using the 0-based index
 * of this territory in the current ordered list.
 */
export function getTerritoryDisplayName(territory: Pick<Territory, 'name'>, index: number): string {
  const trimmed = territory.name?.trim();
  if (trimmed) return trimmed;
  return `Territory ${index + 1}`;
}

export const TERRITORY_PALETTE: TerritoryPaletteEntry[] = [
  { fill: '#3B82F6', stroke: '#1E40AF', badgeBg: '#DBEAFE', badgeText: '#1E40AF' },
  { fill: '#10B981', stroke: '#047857', badgeBg: '#D1FAE5', badgeText: '#065F46' },
  { fill: '#A855F7', stroke: '#7E22CE', badgeBg: '#F3E8FF', badgeText: '#6B21A8' },
  { fill: '#F59E0B', stroke: '#B45309', badgeBg: '#FEF3C7', badgeText: '#92400E' },
  { fill: '#F43F5E', stroke: '#BE123C', badgeBg: '#FFE4E6', badgeText: '#9F1239' },
  { fill: '#06B6D4', stroke: '#0E7490', badgeBg: '#CFFAFE', badgeText: '#155E75' },
  { fill: '#EC4899', stroke: '#BE185D', badgeBg: '#FCE7F3', badgeText: '#9D174D' },
  { fill: '#14B8A6', stroke: '#0F766E', badgeBg: '#CCFBF1', badgeText: '#115E59' },
  { fill: '#F97316', stroke: '#C2410C', badgeBg: '#FFEDD5', badgeText: '#9A3412' },
  { fill: '#6366F1', stroke: '#4338CA', badgeBg: '#E0E7FF', badgeText: '#3730A3' },
];

export function getPaletteEntryByIndex(index: number): TerritoryPaletteEntry {
  return TERRITORY_PALETTE[index % TERRITORY_PALETTE.length];
}

export function getPaletteEntryByFill(fill: string | undefined): TerritoryPaletteEntry | undefined {
  if (!fill) return undefined;
  return TERRITORY_PALETTE.find(entry => entry.fill.toLowerCase() === fill.toLowerCase());
}

export function generateTerritoryId(): string {
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Older mobile builds persisted `Territory.feature` as a flat vertex list
 * (`{ coordinates: [{ latitude, longitude }, ...] }`) instead of a TerraDraw
 * GeoJSON Feature. TerraDraw rejects that shape on restore, so convert it
 * into a valid polygon Feature here. Current mobile builds already write
 * the GeoJSON format.
 */
interface LegacyMobileFeature {
  coordinates: { latitude: number; longitude: number }[];
}

function isLegacyMobileFeature(feature: unknown): feature is LegacyMobileFeature {
  const coords = (feature as LegacyMobileFeature | null)?.coordinates;
  return (
    Array.isArray(coords) &&
    coords.length >= 3 &&
    typeof coords[0]?.latitude === 'number' &&
    typeof coords[0]?.longitude === 'number'
  );
}

/** Matches the drawer's `coordinatePrecision: 5` TerraDraw adapter setting. */
function roundCoord(value: number): number {
  return Math.round(value * 1e5) / 1e5;
}

/**
 * Normalize a loaded territory list so every drawn shape is a TerraDraw-
 * compatible GeoJSON Feature, regardless of which client (or app version)
 * saved it. Territories without a usable shape keep `feature` undefined
 * (zip-only territories).
 */
export function normalizeTerritoryFeatures(territories: Territory[]): Territory[] {
  return territories.map((t, idx) => {
    if (!t.feature || (t.feature as GeoJSONFeature).geometry) return t;
    if (!isLegacyMobileFeature(t.feature)) {
      return { ...t, feature: undefined };
    }
    const ring = t.feature.coordinates.map(p => [roundCoord(p.longitude), roundCoord(p.latitude)]);
    ring.push([...ring[0]]);
    const palette = t.color ?? getPaletteEntryByIndex(idx);
    const feature: GeoJSONFeature = {
      id: crypto.randomUUID(),
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [ring] },
      properties: {
        mode: 'polygon',
        territoryId: t.id,
        territoryColorFill: palette.fill,
        territoryColorIndex: idx,
      },
    };
    return { ...t, feature };
  });
}

/**
 * Accepts the service-area KEY forms actually stored by the editor:
 * US ZIP (5-digit or ZIP+4), a Canadian FSA (3-char, e.g. "T2A"), or a full
 * Canadian postal code (A1A 1A1 / A1A1A1). The trailing local-delivery-unit is
 * optional because Canada service areas are stored as FSAs — see
 * `normalizeServiceAreaCode` / `isValidServiceAreaCode` in utils/addressRegions.
 */
const ZIP_CODE_PATTERN = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z](\s?\d[A-Za-z]\d)?)$/i;

/** True when at least one valid US ZIP or Canadian postal code is present in the service area state. */
export function hasValidServiceAreaZipCodes(
  serviceAreaZipCodes: string[],
  zipTerritoryMap: Record<string, string[]>
): boolean {
  const seen = new Set<string>();
  for (const raw of serviceAreaZipCodes) {
    const zip = raw.trim();
    if (ZIP_CODE_PATTERN.test(zip)) seen.add(zip);
  }
  for (const raw of Object.keys(zipTerritoryMap)) {
    const zip = raw.trim();
    if (ZIP_CODE_PATTERN.test(zip)) seen.add(zip);
  }
  return seen.size > 0;
}
